/**
 * Upload Queue Manager
 * Handles offline photo uploads with IndexedDB storage and automatic retry
 */

const DB_NAME = 'PixUploadQueue';
const DB_VERSION = 1;
const STORE_NAME = 'uploads';

class UploadQueueManager {
  constructor() {
    this.db = null;
    this.listeners = [];
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ Upload queue IndexedDB initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          objectStore.createIndex('status', 'status', { unique: false });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('✅ Created upload queue object store');
        }
      };
    });
  }

  /**
   * Add photo to upload queue
   */
  async addToQueue(photoData, customerPhone, photoId) {
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const uploadItem = {
      photoData: photoData, // Base64 image data
      customerPhone: customerPhone,
      photoId: photoId, // Client-side photo ID for tracking
      timestamp: Date.now(),
      status: 'queued', // queued, uploading, uploaded, failed
      retryCount: 0,
      error: null,
      createdAt: new Date().toISOString()
    };

    return new Promise((resolve, reject) => {
      const request = store.add(uploadItem);

      request.onsuccess = () => {
        const queueId = request.result;
        console.log(`📥 Added photo ${photoId} to upload queue (Queue ID: ${queueId})`);
        this.notifyListeners();
        resolve(queueId);
      };

      request.onerror = () => {
        console.error('Failed to add to queue:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all queued uploads
   */
  async getQueuedUploads() {
    const transaction = this.db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');

    return new Promise((resolve, reject) => {
      const request = index.getAll('queued');

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get all uploads (for debugging)
   */
  async getAllUploads() {
    const transaction = this.db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result || []);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Update upload status
   */
  async updateStatus(queueId, status, error = null) {
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(queueId);

      getRequest.onsuccess = () => {
        const item = getRequest.result;
        if (!item) {
          reject(new Error('Upload not found in queue'));
          return;
        }

        item.status = status;
        item.error = error;
        if (status === 'uploading') {
          item.retryCount += 1;
        }

        const updateRequest = store.put(item);

        updateRequest.onsuccess = () => {
          console.log(`✅ Updated queue item ${queueId} status to: ${status}`);
          this.notifyListeners();
          resolve();
        };

        updateRequest.onerror = () => {
          reject(updateRequest.error);
        };
      };

      getRequest.onerror = () => {
        reject(getRequest.error);
      };
    });
  }

  /**
   * Remove upload from queue
   */
  async removeFromQueue(queueId) {
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.delete(queueId);

      request.onsuccess = () => {
        console.log(`🗑️ Removed queue item ${queueId}`);
        this.notifyListeners();
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get queue count by status
   */
  async getQueueCount(status = null) {
    const transaction = this.db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      if (status) {
        const index = store.index('status');
        const request = index.count(status);

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(request.error);
        };
      } else {
        const request = store.count();

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(request.error);
        };
      }
    });
  }

  /**
   * Add listener for queue changes
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners() {
    this.listeners.forEach(callback => callback());
  }

  /**
   * Clear all completed uploads (older than 24 hours)
   */
  async clearOldUploads() {
    const transaction = this.db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const dayAgo = Date.now() - (24 * 60 * 60 * 1000);

    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const item = cursor.value;
          if (item.status === 'uploaded' && item.timestamp < dayAgo) {
            cursor.delete();
            deletedCount++;
          }
          cursor.continue();
        } else {
          console.log(`🗑️ Cleared ${deletedCount} old uploads from queue`);
          resolve(deletedCount);
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}

// Create singleton instance
const uploadQueue = new UploadQueueManager();

export default uploadQueue;

